import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { MdClose } from "react-icons/md";
import { useDispatch } from "react-redux";
import { updateAddress } from "../../store/slices/AddressSlice";
import { toast } from "react-toastify";
import "./style.css";

const EditAddressModal = ({ open, onClose, address, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    address: "",
    landmark: "",
    city: "",
    state: "",
    pincode: "",
    addressType: "home",
    isDefault: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  // Populate form data when address prop changes
  useEffect(() => {
    if (address) {
      setFormData({
        name: address.name || "",
        phoneNumber: address.phoneNumber || "",
        email: address.email || "",
        address: address.address || "",
        landmark: address.landmark || "",
        city: address.city || "",
        state: address.state || "",
        pincode: address.pincode || "",
        addressType: address.addressType || "home",
        isDefault: address.isDefault || false,
      });
    }
  }, [address]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[0-9]{10}$/.test(formData.phoneNumber.trim())) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^[0-9]{6}$/.test(formData.pincode.trim())) {
      newErrors.pincode = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || !address) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await dispatch(
        updateAddress({
          id: address.id,
          addressData: formData,
        })
      ).unwrap();

      toast.success("Address updated successfully!");

      if (onSuccess) {
        onSuccess(result);
      }

      onClose();
    } catch (error) {
      toast.error(error || "Failed to update address");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className="edit-address-modal"
    >
      <DialogTitle>
        <div className="modal-header">
          <span>Edit Address</span>
          <IconButton onClick={handleClose} disabled={isLoading}>
            <MdClose />
          </IconButton>
        </div>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                required
                disabled={isLoading}
                placeholder="1234567890"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
                required
                disabled={isLoading}
                placeholder="your@email.com"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: "13px" }}>Address Type</InputLabel>
                <Select
                  name="addressType"
                  value={formData.addressType}
                  onChange={handleInputChange}
                  label="Address Type"
                  disabled={isLoading}
                  sx={{
                    fontSize: "13px",
                    "& .MuiSelect-select": {
                      fontSize: "13px",
                    },
                  }}
                >
                  <MenuItem value="home" sx={{ fontSize: "13px" }}>
                    Home
                  </MenuItem>
                  <MenuItem value="work" sx={{ fontSize: "13px" }}>
                    Work
                  </MenuItem>
                  <MenuItem value="other" sx={{ fontSize: "13px" }}>
                    Other
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                error={!!errors.address}
                helperText={errors.address}
                required
                multiline
                rows={2}
                disabled={isLoading}
                placeholder="House/Flat No, Street Name, Area"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Landmark (Optional)"
                name="landmark"
                value={formData.landmark}
                onChange={handleInputChange}
                disabled={isLoading}
                placeholder="Near Metro Station, Mall, etc."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                error={!!errors.city}
                helperText={errors.city}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                error={!!errors.state}
                helperText={errors.state}
                required
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleInputChange}
                error={!!errors.pincode}
                helperText={errors.pincode}
                required
                disabled={isLoading}
                placeholder="123456"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefault}
                    onChange={handleInputChange}
                    name="isDefault"
                    disabled={isLoading}
                    color="primary"
                    sx={{
                      "&.Mui-checked": {
                        color: "#4caf50",
                      },
                      ml: 1,
                    }}
                  />
                }
                label="Set as default address"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ pt: 0 }}>
          <Button onClick={handleClose} disabled={isLoading} variant="outlined">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            className="submit-btn"
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} color="inherit" />
                <span style={{ marginLeft: 8 }}>Updating...</span>
              </>
            ) : (
              "Update Address"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditAddressModal;
